import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, CircularProgress } from "@mui/material";
import { Link } from "react-router-dom";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { getDashboard } from "services/dashboard";
import "./dashboard.css";
const currency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
const count = (value) => (value == null ? "—" : new Intl.NumberFormat("pt-BR").format(value));
const monthLabel = (value) =>
  new Intl.DateTimeFormat("pt-BR", { month: "short", timeZone: "UTC" })
    .format(new Date(`${value}-01T12:00:00Z`))
    .replace(".", "");
function Dashboard({ user }) {
  const [data, setData] = useState(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState("");
  const generation = useRef(0);
  const load = useCallback(async () => {
    const current = ++generation.current;
    if (!user?.uid) return;
    setLoading(true);
    setError("");
    try {
      const result = await getDashboard();
      if (current === generation.current) setData(result);
    } catch (failure) {
      if (current === generation.current)
        setError(failure.message || "Não foi possível carregar o painel.");
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, [user?.uid]);
  useEffect(() => {
    setData(null);
    load();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 60000);
    return () => {
      generation.current++;
      clearInterval(timer);
    };
  }, [load]);
  const metrics = data
    ? [
        ["Publicações cadastradas", count(data.publications), "Obras salvas na sua conta"],
        ["Pedidos pagos", count(data.paidOrders), `De ${count(data.orders)} pedidos sincronizados`],
        ["Exemplares vendidos", count(data.units), "Unidades dos seus livros em pedidos pagos"],
        [
          "Valor bruto dos livros",
          data.grossBookAmount == null ? "—" : currency(data.grossBookAmount),
          "Preço dos itens × quantidade; sem frete",
        ],
      ]
    : [];
  const maxUnits = Math.max(1, ...(data?.months || []).map((item) => item.units));
  return (
    <DashboardLayout>
      <main className="author-dashboard">
        <header className="author-dashboard__header">
          <div>
            <span className="author-dashboard__eyebrow">SUA OBRA EM NÚMEROS</span>
            <h1>Painel do autor</h1>
            <p>Acompanhe suas publicações e os pedidos registrados na plataforma.</p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading || !user?.uid}
            className="author-dashboard__refresh"
          >
            <RefreshRoundedIcon />
            {loading ? "Atualizando…" : "Atualizar"}
          </button>
        </header>
        {error && (
          <Alert severity="error">
            {error} {data && "Os últimos dados carregados foram preservados."}
          </Alert>
        )}
        {!data && loading && (
          <div className="author-dashboard__loading" role="status">
            <CircularProgress size={26} />
            Carregando seus dados…
          </div>
        )}
        {!data && !loading && error && (
          <button className="author-dashboard__refresh" onClick={load}>
            Tentar novamente
          </button>
        )}
        {data && (
          <>
            <div className="author-dashboard__updated">
              Consultado em {new Date(data.checkedAt).toLocaleString("pt-BR")}
              {loading ? " · Atualizando" : ""}
            </div>
            {!!data.incompleteOrders && (
              <Alert severity="warning">
                Há {data.incompleteOrders} pedido(s) pago(s) sem os detalhes completos dos itens. Os
                totais de exemplares e valores ficarão indisponíveis até a sincronização desses
                dados.
              </Alert>
            )}
            <section className="author-dashboard__metrics" aria-label="Resumo da sua conta">
              {metrics.map(([label, value, hint]) => (
                <article key={label}>
                  <h2>{label}</h2>
                  <strong>{value}</strong>
                  <p>{hint}</p>
                </article>
              ))}
            </section>
            <div className="author-dashboard__grid">
              <section className="author-dashboard__panel">
                <header>
                  <div>
                    <h2>Exemplares em pedidos pagos</h2>
                    <p>Últimos seis meses · agrupados pela data do pedido</p>
                  </div>
                  <span className="author-dashboard__tag">Seus livros</span>
                </header>
                {data.incompleteOrders ? (
                  <p className="author-dashboard__empty">
                    Aguardando os detalhes dos pedidos para montar o histórico.
                  </p>
                ) : (
                  <>
                    <div
                      className="author-dashboard__chart"
                      aria-label="Exemplares vendidos por mês"
                    >
                      {data.months.map((item) => (
                        <div className="author-dashboard__column" key={item.month}>
                          <span>{count(item.units)}</span>
                          <div className="author-dashboard__track">
                            <div style={{ height: `${(item.units / maxUnits) * 100}%` }} />
                          </div>
                          <time dateTime={item.month} title={item.month}>{monthLabel(item.month)}</time>
                        </div>
                      ))}
                    </div>
                    {!data.months.some((item) => item.units > 0) && (
                      <p className="author-dashboard__chart-note">
                        Nenhum exemplar em pedidos pagos neste período.
                      </p>
                    )}
                    <table className="author-dashboard__sr-only">
                      <caption>Exemplares por mês</caption>
                      <thead>
                        <tr>
                          <th>Mês</th>
                          <th>Exemplares</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.months.map((item) => (
                          <tr key={item.month}>
                            <th>{monthLabel(item.month)}</th>
                            <td>{item.units}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </section>
              <section className="author-dashboard__panel">
                <header>
                  <div>
                    <h2>Suas publicações</h2>
                    <p>Vínculo das obras com o catálogo da Tray</p>
                  </div>
                  <MenuBookRoundedIcon />
                </header>
                <div className="author-dashboard__publication">
                  <span>Com produto vinculado na Tray</span>
                  <strong>{count(data.linkedPublications)}</strong>
                </div>
                <div className="author-dashboard__publication">
                  <span>Sem produto vinculado</span>
                  <strong>{count(data.unlinkedPublications)}</strong>
                </div>
                <p className="author-dashboard__note">
                  O vínculo indica que o produto foi cadastrado. Não representa aprovação editorial
                  nem disponibilidade na vitrine.
                </p>
                <Link to="/my-publications" className="author-dashboard__link">
                  Ver minhas publicações <ArrowForwardRoundedIcon />
                </Link>
              </section>
            </div>
            <section className="author-dashboard__panel">
              <header>
                <div>
                  <h2>Livros mais vendidos</h2>
                  <p>Exemplares nos pedidos pagos sincronizados · todo o histórico disponível</p>
                </div>
                <Link to="/my-orders" className="author-dashboard__link">
                  Ver pedidos <ArrowForwardRoundedIcon />
                </Link>
              </header>
              {data.topBooks.length ? (
                <ol className="author-dashboard__ranking">
                  {data.topBooks.map((book) => (
                    <li key={book.id}>
                      <span>{book.title}</span>
                      <strong>
                        {count(book.units)} {book.units === 1 ? "exemplar" : "exemplares"}
                      </strong>
                      <span>{currency(book.amount)}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="author-dashboard__empty">
                  {data.incompleteOrders
                    ? "Aguardando os detalhes dos itens vendidos."
                    : "Ainda não há vendas pagas registradas para seus livros."}
                </p>
              )}
            </section>
            <p className="author-dashboard__footnote">
              Fonte: publicações e pedidos da Tray sincronizados com a PSO. Pedidos cancelados ou
              estornados ficam fora das vendas. O valor bruto considera os preços dos itens e não
              representa o repasse ao autor; não desconta cupons, taxas ou custos de impressão. Este
              ambiente de testes também inclui compras de teste registradas na loja.
            </p>
          </>
        )}
      </main>
    </DashboardLayout>
  );
}
export default Dashboard;
